// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: three-way merge of text by line, for the shared scratchpad. Two people who
// edited different lines both keep their changes; the same line changed two ways is a
// conflict. Insertions at the same spot are kept from both sides (ours first) rather
// than fought over, which is what a to-do list wants.

export interface LineMergeResult {
	/** True when both sides changed the same lines differently; `text` is then `ours`. */
	conflict: boolean;
	text: string;
}

interface Hunk {
	/** Base line range [start, end) that this hunk replaces. */
	start: number;
	end: number;
	lines: Array<string>;
}

/** Longest common subsequence table of two line arrays, for the diff below. */
function lcsTable(a: Array<string>, b: Array<string>): Array<Uint32Array> {
	const table: Array<Uint32Array> = [];
	for (let i = 0; i <= a.length; i++) table.push(new Uint32Array(b.length + 1));
	for (let i = a.length - 1; i >= 0; i--) {
		for (let j = b.length - 1; j >= 0; j--) {
			table[i][j] = a[i] === b[j] ? table[i + 1][j + 1] + 1 : Math.max(table[i + 1][j], table[i][j + 1]);
		}
	}
	return table;
}

/** Edits turning `base` into `next`, as hunks over base line ranges. */
function diffHunks(base: Array<string>, next: Array<string>): Array<Hunk> {
	const table = lcsTable(base, next);
	const hunks: Array<Hunk> = [];
	let i = 0;
	let j = 0;
	let open: Hunk | null = null;
	const flush = () => {
		if (open) hunks.push(open);
		open = null;
	};
	while (i < base.length || j < next.length) {
		if (i < base.length && j < next.length && base[i] === next[j]) {
			flush();
			i++;
			j++;
		} else if (j < next.length && (i >= base.length || table[i][j + 1] >= table[i + 1][j])) {
			// A line only in `next`: an insertion.
			if (!open) open = {start: i, end: i, lines: []};
			open.lines.push(next[j]);
			j++;
		} else {
			// A base line missing from `next`: a deletion.
			if (!open) open = {start: i, end: i, lines: []};
			open.end = i + 1;
			i++;
		}
	}
	flush();
	return hunks;
}

function sameLines(a: Array<string>, b: Array<string>): boolean {
	return a.length === b.length && a.every((line, index) => line === b[index]);
}

/**
 * Merges `ours` and `theirs`, both derived from `base`. Non-overlapping edits combine;
 * identical edits collapse; pure insertions at one spot are kept from both sides.
 */
export function mergeLines(base: string, ours: string, theirs: string): LineMergeResult {
	if (ours === theirs) return {conflict: false, text: ours};
	if (base === ours) return {conflict: false, text: theirs};
	if (base === theirs) return {conflict: false, text: ours};
	const split = (text: string) => (text === '' ? [] : text.split('\n'));
	const baseLines = split(base);
	const ourHunks = diffHunks(baseLines, split(ours));
	const theirHunks = diffHunks(baseLines, split(theirs));
	const out: Array<string> = [];
	let cursor = 0;
	let a = 0;
	let b = 0;
	while (a < ourHunks.length || b < theirHunks.length) {
		const mine = ourHunks[a];
		const other = theirHunks[b];
		const next = !other || (mine && mine.start <= other.start) ? mine : other;
		// Copy untouched base lines up to the next hunk.
		while (cursor < next.start) out.push(baseLines[cursor++]);
		const overlaps =
			mine &&
			other &&
			mine.start < other.end + (other.end === other.start ? 1 : 0) &&
			other.start < mine.end + (mine.end === mine.start ? 1 : 0);
		if (mine && other && overlaps) {
			if (mine.start === mine.end && other.start === other.end && mine.start === other.start) {
				// Both inserted at the same place: keep both, ours first.
				out.push(...mine.lines, ...other.lines);
			} else if (mine.start === other.start && mine.end === other.end && sameLines(mine.lines, other.lines)) {
				out.push(...mine.lines);
			} else if (mine.start === mine.end || other.start === other.end) {
				// One side inserted next to lines the other side changed: keep both, in base order.
				const [first, second] = mine.start <= other.start ? [mine, other] : [other, mine];
				out.push(...first.lines);
				if (second.start > first.start) while (cursor < second.start) out.push(baseLines[cursor++]);
				out.push(...second.lines);
				cursor = Math.max(cursor, first.end, second.end);
				a++;
				b++;
				continue;
			} else {
				return {conflict: true, text: ours};
			}
			cursor = Math.max(mine.end, other.end);
			a++;
			b++;
			continue;
		}
		out.push(...next.lines);
		cursor = Math.max(cursor, next.end);
		if (next === mine) a++;
		else b++;
	}
	while (cursor < baseLines.length) out.push(baseLines[cursor++]);
	return {conflict: false, text: out.join('\n')};
}
